import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import Container from '../../../../components/Container';
import AlertMsg from '../../../../components/AlertMsg';
import RestaurantHeader from '../../../../components/RestaurantHeader';

import {
  CustomerList,
  CustomerGrowthChart,
  TopRestaurantsTable,
} from '../components';
import {
  useGetCustomers,
  useGetCustomerStats,
  useGetCustomerGrowth,
  useGetTopRestaurants,
} from '../services';
import { useExportCustomersExcel } from '../hooks/use-export-customers-excel';

const CustomersPage = () => {
  const { rest_id: restaurantId } = useParams<{ rest_id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });

  // Date range for growth chart (default: last 30 days)
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [fromDate, setFromDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);

  // Top restaurants limit
  const [topLimit, setTopLimit] = useState(10);

  // Fetch data
  const { data: customersData, isLoading: customersLoading } = useGetCustomers(
    restaurantId || '',
    page,
    limit
  );
  const { data: stats, isLoading: statsLoading } = useGetCustomerStats(restaurantId);
  const { data: growthData, isLoading: growthLoading } = useGetCustomerGrowth(
    fromDate,
    toDate,
    restaurantId
  );
  const { data: topRestaurants, isLoading: topRestaurantsLoading } =
    useGetTopRestaurants(topLimit);

  const { exportToExcel, isPending: isExporting } = useExportCustomersExcel(setMsgAlert);

  const handleExportToExcel = () => {
    if (!restaurantId) return;
    exportToExcel(restaurantId, restaurantId);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleApplyDateRange = () => {
    // Refetch is automatic when fromDate/toDate change
    setMsgAlert({
      open: true,
      message: 'Date range updated',
      msgType: 'success',
    });
  };

  return (
    <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title="Customers"
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}`)}
            />
          </Grid>

          <Grid item xs={12} sx={styles.mainView} px={3} pt={2}>
            {/* Tabs Navigation */}
            <Box sx={styles.tabsContainer}>
              <Tabs value={activeTab} onChange={handleTabChange} sx={styles.tabs}>
                <Tab label="Customer List" sx={styles.tab} />
                <Tab label="Analytics" sx={styles.tab} />
                <Tab label="Top Restaurants" sx={styles.tab} />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={styles.tabContent}>
              {/* Tab 1: Customer List */}
              {activeTab === 0 && (
                <Box>
                  <CustomerList
                    customers={customersData?.rows || []}
                    totalCount={customersData?.count || 0}
                    page={page}
                    limit={limit}
                    onPageChange={handlePageChange}
                    isLoading={customersLoading}
                    onExport={handleExportToExcel}
                    isExporting={isExporting}
                  />
                </Box>
              )}

              {/* Tab 2: Analytics (Combined Statistics & Growth) */}
              {activeTab === 1 && (
                <Box>
                  {/* Date Range Picker */}
                  <Box sx={styles.dateRangeContainer}>
                    <TextField
                      label="From Date"
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={styles.dateInput}
                    />
                    <TextField
                      label="To Date"
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={styles.dateInput}
                    />
                    <Button onClick={handleApplyDateRange} sx={styles.applyBtn}>
                      Apply
                    </Button>
                  </Box>

                  {/* Growth Chart with Stats */}
                  <CustomerGrowthChart
                    growthData={growthData || []}
                    isLoading={growthLoading}
                    totalCustomers={stats?.totalCustomers}
                  />
                </Box>
              )}

              {/* Tab 3: Top Restaurants */}
              {activeTab === 2 && (
                <Box>
                  {/* Top Limit Selector */}
                  <Box sx={styles.topLimitContainer}>
                    <TextField
                      label="Show Top"
                      type="number"
                      value={topLimit}
                      onChange={(e) => setTopLimit(Number(e.target.value))}
                      inputProps={{ min: 1, max: 20 }}
                      sx={styles.topLimitInput}
                    />
                  </Box>

                  {/* Top Restaurants Table */}
                  <TopRestaurantsTable
                    topRestaurants={topRestaurants || []}
                    isLoading={topRestaurantsLoading}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <AlertMsg
          msgAlert={msgAlert}
          onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
        />
      </Container>
  );
};

export default CustomersPage;

const styles = {
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  tabsContainer: {
    borderBottom: '1px solid #eee',
    marginBottom: 3,
  },
  tabs: {
    '& .MuiTabs-indicator': {
      backgroundColor: '#000',
    },
  },
  tab: {
    fontFamily: 'GilroyBold',
    fontSize: 14,
    textTransform: 'capitalize' as const,
    color: '#666',
    '&.Mui-selected': {
      color: '#000',
    },
  },
  tabContent: {
    paddingTop: 2,
  },
  dateRangeContainer: {
    display: 'flex',
    gap: 2,
    marginBottom: 3,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  dateInput: {
    minWidth: '200px',
  },
  applyBtn: {
    backgroundColor: '#000',
    color: '#fff',
    fontSize: 14,
    borderRadius: '8px',
    textTransform: 'capitalize' as const,
    paddingLeft: '2rem',
    paddingRight: '2rem',
    paddingTop: '10px',
    paddingBottom: '10px',
    '&:hover': {
      backgroundColor: '#333',
    },
  },
  topLimitContainer: {
    marginBottom: 3,
  },
  topLimitInput: {
    maxWidth: '150px',
  },
};
